import React, { useState, useEffect, useCallback, useMemo } from "react";
import { apiFetch } from "../utils/api";
import { useFeedback } from "../hooks/useFeedback";
import Spinner from "./ui/Spinner";
import { IconTrash } from "./ui/icons";
import ConfirmDelete from "./ui/ConfirmDelete";
import CollapsibleCard from "./ui/CollapsibleCard";

function IconPause() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
      <path d="M5.75 3a.75.75 0 00-.75.75v12.5c0 .414.336.75.75.75h1.5a.75.75 0 00.75-.75V3.75A.75.75 0 007.25 3h-1.5zM12.75 3a.75.75 0 00-.75.75v12.5c0 .414.336.75.75.75h1.5a.75.75 0 00.75-.75V3.75a.75.75 0 00-.75-.75h-1.5z" />
    </svg>
  );
}

function IconPlay() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
      <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
    </svg>
  );
}

function IconChevron({ open }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor"
      className={`w-3.5 h-3.5 text-gray-400 transition-transform ${open ? "rotate-90" : ""}`}>
      <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
    </svg>
  );
}

// Construit les groupes campagne → sites à partir des deux sources de données.
// Un site apparaît sous une seule campagne (premier match gagne).
// Les sites non rattachés à aucune campagne vont dans "orphans".
function buildGroups(formsSites, sitesMap) {
  if (!formsSites || !sitesMap) return { campaigns: [], orphans: [] };

  const claimed = new Set();
  const campaigns = [];

  for (const form of formsSites.forms || []) {
    const siteIds = [
      ...new Set(
        (form.assignments || [])
          .map(a => a.site_id)
          .filter(sid => sid && sitesMap[sid])
      ),
    ].filter(sid => !claimed.has(sid));

    siteIds.forEach(sid => claimed.add(sid));

    if (siteIds.length > 0) {
      campaigns.push({
        form_id: form.form_id,
        name: form.form_name || `Formulaire ${form.form_id}`,
        sites: siteIds
          .map(sid => sitesMap[sid])
          .sort((a, b) => a.name.localeCompare(b.name)),
      });
    }
  }

  const orphans = Object.values(sitesMap)
    .filter(s => !claimed.has(s.id))
    .sort((a, b) => {
      if (a.paused !== b.paused) return a.paused ? 1 : -1;
      return a.name.localeCompare(b.name);
    });

  return { campaigns, orphans };
}

function SiteRow({ site, onTogglePause, onDelete, loadingId, indent }) {
  const [confirming, setConfirming] = useState(false);
  const isLoading = loadingId === site.id;

  return (
    <div className={`flex flex-col gap-2 py-2.5 border-b border-gray-50 last:border-0
      ${indent ? "pl-6 pr-3" : "px-3"}
      ${site.paused ? "bg-amber-50/40" : ""}
    `}>
      <div className="flex items-center gap-2 min-w-0">
        <span className={`shrink-0 w-2 h-2 rounded-full ${site.paused ? "bg-amber-400" : "bg-emerald-400"}`} />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-sm font-medium text-gray-800 truncate">{site.name}</span>
            {site.paused && <span className="badge badge-paused">en pause</span>}
          </div>
          <span className="text-xs text-gray-400 font-mono">{site.id}</span>
        </div>
        {!confirming ? (
          <div className="flex items-center gap-1 shrink-0">
            <button
              onClick={() => onTogglePause(site.id, site.paused)}
              disabled={isLoading}
              title={site.paused ? "Reprendre" : "Mettre en pause"}
              className={`btn btn-icon btn-sm ${site.paused ? "btn-success" : "btn-secondary"}`}
            >
              {isLoading ? <Spinner /> : site.paused ? <IconPlay /> : <IconPause />}
            </button>
            <button
              onClick={() => setConfirming(true)}
              disabled={isLoading}
              title="Supprimer"
              className="btn btn-icon btn-sm btn-ghost text-red-400 hover:bg-red-50 hover:text-red-600"
            >
              <IconTrash />
            </button>
          </div>
        ) : (
          <ConfirmDelete
            onConfirm={() => { setConfirming(false); onDelete(site.id); }}
            onCancel={() => setConfirming(false)}
          />
        )}
      </div>
    </div>
  );
}

function CampaignSection({ campaign, isArchived, archiving, onArchive, onUnarchive, onTogglePause, onDelete, loadingId }) {
  const [open, setOpen] = useState(!isArchived);
  const [confirming, setConfirming] = useState(false);
  const pausedCount = campaign.sites.filter(s => s.paused).length;

  if (isArchived) {
    return (
      <div className="border-b border-gray-100 last:border-0 bg-gray-50/60">
        <div className="flex items-center gap-2 px-3 py-2">
          <IconChevron open={false} />
          <span className="text-xs font-semibold text-gray-400 flex-1 truncate italic">{campaign.name}</span>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
              {campaign.sites.length} site{campaign.sites.length > 1 ? "s" : ""} archivés
            </span>
            {!confirming ? (
              <button
                onClick={() => setConfirming(true)}
                disabled={archiving}
                className="btn btn-xs btn-secondary"
              >
                {archiving ? <Spinner /> : "Désarchiver"}
              </button>
            ) : (
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500">Confirmer ?</span>
                <button onClick={() => { setConfirming(false); onUnarchive(campaign.form_id); }} className="btn btn-xs btn-success">Oui</button>
                <button onClick={() => setConfirming(false)} className="btn btn-xs btn-ghost">Non</button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border-b border-gray-100 last:border-0">
      <div className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 transition-colors">
        <button onClick={() => setOpen(o => !o)} className="flex items-center gap-2 flex-1 min-w-0 text-left">
          <IconChevron open={open} />
          <span className="text-xs font-semibold text-gray-700 truncate">{campaign.name}</span>
        </button>
        <div className="flex items-center gap-1.5 shrink-0">
          {pausedCount > 0 && (
            <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
              {pausedCount} en pause
            </span>
          )}
          <span className="text-xs text-gray-400">
            {campaign.sites.length} site{campaign.sites.length > 1 ? "s" : ""}
          </span>
          {!confirming ? (
            <button
              onClick={() => setConfirming(true)}
              disabled={archiving}
              className="btn btn-xs btn-secondary"
            >
              {archiving ? <Spinner /> : "Archiver"}
            </button>
          ) : (
            <div className="flex items-center gap-1">
              <span className="text-xs text-gray-500">Archiver les {campaign.sites.length} sites ?</span>
              <button onClick={() => { setConfirming(false); onArchive(campaign.form_id, campaign.name, campaign.sites.map(s => s.id)); }} className="btn btn-xs btn-primary">Oui</button>
              <button onClick={() => setConfirming(false)} className="btn btn-xs btn-ghost">Non</button>
            </div>
          )}
        </div>
      </div>
      {open && campaign.sites.map(site => (
        <SiteRow
          key={site.id}
          site={site}
          onTogglePause={onTogglePause}
          onDelete={onDelete}
          loadingId={loadingId}
          indent
        />
      ))}
    </div>
  );
}

function OrphanSection({ sites, onTogglePause, onDelete, loadingId }) {
  const [open, setOpen] = useState(true);

  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 transition-colors"
      >
        <IconChevron open={open} />
        <span className="text-xs font-semibold text-gray-500 flex-1 italic">Sans campagne</span>
        <span className="text-xs text-gray-400">
          {sites.length} site{sites.length > 1 ? "s" : ""}
        </span>
      </button>
      {open && sites.map(site => (
        <SiteRow
          key={site.id}
          site={site}
          onTogglePause={onTogglePause}
          onDelete={onDelete}
          loadingId={loadingId}
          indent
        />
      ))}
    </div>
  );
}

export default function GestionDesSites() {
  const [sitesMap, setSitesMap] = useState({});
  const [formsSites, setFormsSites] = useState(null);
  const [archiveRegistry, setArchiveRegistry] = useState({});
  const [archivingId, setArchivingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingId, setLoadingId] = useState(null);
  const [siteId, setSiteId] = useState("");
  const [pressureId, setPressureId] = useState("");
  const [adding, setAdding] = useState(false);
  const [feedback, showFeedback] = useFeedback();

  const { campaigns, orphans } = useMemo(
    () => buildGroups(formsSites, sitesMap),
    [formsSites, sitesMap]
  );

  const totalSites = Object.keys(sitesMap).length;

  const fetchAll = useCallback(async () => {
    try {
      const [sitesData, formsData, archivesData] = await Promise.all([
        apiFetch(`/api/radar/sites_from_env?nocache=${Date.now()}`),
        apiFetch("/api/assoc/forms-sites"),
        apiFetch("/api/radar/campaign_archives"),
      ]);
      const map = {};
      for (const [id, s] of Object.entries(sitesData.sites || {})) {
        map[id] = { id, name: s?.name || "Inconnu", paused: !!s?.paused };
      }
      setSitesMap(map);
      setFormsSites(formsData);
      setArchiveRegistry(archivesData?.campaigns || {});
    } catch {
      showFeedback("error", "Impossible de charger les sites.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const togglePause = async (id, wasPaused) => {
    setLoadingId(id);
    const endpoint = wasPaused ? "resume_site" : "pause_site";
    try {
      await apiFetch(`/api/radar/${endpoint}?site_id=${encodeURIComponent(id)}`, { method: "POST" });
      setSitesMap(prev => ({ ...prev, [id]: { ...prev[id], paused: !wasPaused } }));
    } catch (e) {
      showFeedback("error", e.message);
    } finally {
      setLoadingId(null);
    }
  };

  const handleDelete = async (id) => {
    setLoadingId(id);
    try {
      await apiFetch(`/api/radar/delete_site/?site_id=${encodeURIComponent(id)}`, { method: "DELETE" });
      setSitesMap(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      showFeedback("success", "Site supprimé.");
    } catch (e) {
      showFeedback("error", e.message);
    } finally {
      setLoadingId(null);
    }
  };

  const handleArchive = async (formId, formName, siteIds) => {
    setArchivingId(formId);
    try {
      await apiFetch("/api/radar/archive_campaign", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ campaign_form_id: formId, campaign_name: formName, site_ids: siteIds }),
      });
      setArchiveRegistry(prev => ({ ...prev, [String(formId)]: { step_reached: 3 } }));
      showFeedback("success", `Campagne « ${formName} » archivée.`);
    } catch (e) {
      showFeedback("error", e.message);
    } finally {
      setArchivingId(null);
    }
  };

  const handleUnarchive = async (formId) => {
    setArchivingId(formId);
    try {
      const res = await apiFetch("/api/radar/unarchive_campaign", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ campaign_form_id: formId }),
      });
      setArchiveRegistry(prev => {
        const next = { ...prev };
        delete next[String(formId)];
        return next;
      });
      // La restauration reconstruit les fichiers Kizeo supprimés par l'archivage (forms_full depuis
      // les copies archivées, forms_data re-téléchargé depuis Kizeo). Si Kizeo était injoignable,
      // la campagne est quand même désarchivée : on le signale pour relancer plus tard.
      const restored = res?.restored || {};
      const nForms = Object.values(restored.forms_full_rebuilt || {}).reduce((a, n) => a + n, 0);
      const errors = restored.forms_data?.errors || [];
      if (errors.length) {
        showFeedback("error",
          `Campagne désarchivée (${nForms} formulaire(s) restauré(s)), mais le re-téléchargement Kizeo a échoué : ${errors.join(" ; ")}. Relancer un ingest Kizeo plus tard.`);
      } else {
        showFeedback("success", `Campagne désarchivée — ${nForms} formulaire(s) restauré(s).`);
      }
    } catch (e) {
      showFeedback("error", e.message);
    } finally {
      setArchivingId(null);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!siteId.trim() || !pressureId.trim()) {
      showFeedback("error", "Les deux champs sont obligatoires.");
      return;
    }
    setAdding(true);
    try {
      const data = await apiFetch(
        `/api/radar/add_site/?site_id=${encodeURIComponent(siteId.trim())}&pressure_id=${encodeURIComponent(pressureId.trim())}`,
        { method: "POST" }
      );
      const newId = siteId.trim();
      const newSite = { id: newId, name: data.site_info?.name || "Inconnu", paused: false };
      // Nouveau site sans campagne → apparaît dans "Sans campagne"
      setSitesMap(prev => ({ ...prev, [newId]: newSite }));
      setSiteId("");
      setPressureId("");
      showFeedback("success", `Site « ${newSite.name} » ajouté.`);
    } catch (e) {
      showFeedback("error", e.message);
    } finally {
      setAdding(false);
    }
  };

  return (
    <CollapsibleCard
      icon="🌐"
      title="Gestion des sites"
      badge={!loading ? `${totalSites} site${totalSites > 1 ? "s" : ""}` : null}
    >
      {feedback && (
        <div className={`mx-4 mt-3 feedback-${feedback.type === "success" ? "success" : "error"}`}>
          {feedback.msg}
        </div>
      )}
      <div className={feedback ? "" : "mt-1"}>
        {loading ? (
          <div className="px-4 py-6 text-sm text-gray-400 text-center">Chargement…</div>
        ) : totalSites === 0 ? (
          <div className="px-4 py-6 text-sm text-gray-400 text-center">Aucun site configuré.</div>
        ) : (
          <>
            {campaigns.map(campaign => (
              <CampaignSection
                key={campaign.form_id}
                campaign={campaign}
                isArchived={!!archiveRegistry[String(campaign.form_id)]}
                archiving={archivingId === campaign.form_id}
                onArchive={handleArchive}
                onUnarchive={handleUnarchive}
                onTogglePause={togglePause}
                onDelete={handleDelete}
                loadingId={loadingId}
              />
            ))}
            {orphans.length > 0 && (
              <OrphanSection
                sites={orphans}
                onTogglePause={togglePause}
                onDelete={handleDelete}
                loadingId={loadingId}
              />
            )}
          </>
        )}
      </div>
      <div className="divider" />
      <form onSubmit={handleAdd} className="px-4 pb-4 flex flex-col gap-2">
        <p className="text-sm font-semibold text-gray-700">Ajouter un site</p>
        <input className="input" placeholder="ID du site Syrinix" value={siteId} onChange={e => setSiteId(e.target.value)} />
        <input className="input" placeholder="ID de pression (kit)" value={pressureId} onChange={e => setPressureId(e.target.value)} />
        <button type="submit" disabled={adding} className="btn btn-md btn-primary w-full">
          {adding ? "Ajout en cours…" : "+ Ajouter"}
        </button>
      </form>
    </CollapsibleCard>
  );
}
